import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  Upload, 
  FileText, 
  Wand2, 
  Download,
  Eye,
  BarChart3,
  Target,
  BookOpen,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const StoryGeneratorAdmin = () => {
  const [originalStory, setOriginalStory] = useState('');
  const [storyTitle, setStoryTitle] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState('de');
  const [targetLevel, setTargetLevel] = useState('A1');
  const [maxWords, setMaxWords] = useState(200);
  const [preserveStructure, setPreserveStructure] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedStory, setGeneratedStory] = useState(null);
  const [generatedStories, setGeneratedStories] = useState([]);
  const [levelAnalysis, setLevelAnalysis] = useState({});
  const [activeTab, setActiveTab] = useState('generator');
  const { toast } = useToast();

  // Konstanten
  const LANGUAGES = {
    'de': 'Deutsch',
    'en': 'English', 
    'fr': 'Français',
    'es': 'Español',
    'it': 'Italiano',
    'tr': 'Türkçe'
  };

  const LANGUAGE_LEVELS = {
    'A1': 'A1 - Anfänger',
    'A2': 'A2 - Grundkenntnisse',
    'B1': 'B1 - Mittelstufe',
    'B2': 'B2 - Selbstständig',
    'C1': 'C1 - Fortgeschritten',
    'C2': 'C2 - Fachkundig'
  };

  // API-Aufrufe
  const generateStory = async () => {
    if (!originalStory.trim() || !storyTitle.trim()) {
      toast({
        title: "Fehler",
        description: "Bitte geben Sie einen Titel und eine Story ein",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/stories/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: storyTitle,
          original_story: originalStory,
          source_language: sourceLanguage,
          target_level: targetLevel,
          max_words: maxWords,
          preserve_structure: preserveStructure
        })
      });

      if (response.ok) {
        const generated = await response.json();
        setGeneratedStory(generated);
        await fetchGeneratedStories();
        toast({
          title: "Erfolg",
          description: `Story für Level ${targetLevel} erfolgreich generiert`
        });
      } else {
        throw new Error('Generation fehlgeschlagen');
      }
    } catch (error) {
      console.error('Error generating story:', error);
      toast({
        title: "Fehler",
        description: "Story-Generierung fehlgeschlagen",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const fetchGeneratedStories = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/stories/generated?limit=50`);
      if (response.ok) {
        const data = await response.json();
        setGeneratedStories(data);
      }
    } catch (error) {
      console.error('Error fetching generated stories:', error);
    }
  };

  const fetchLevelAnalysis = async (level) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/stories/level-analysis/${level}`);
      if (response.ok) {
        const data = await response.json();
        setLevelAnalysis(prev => ({ ...prev, [level]: data }));
      }
    } catch (error) {
      console.error('Error fetching level analysis:', error);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/plain') {
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalStory(e.target.result);
        if (!storyTitle) {
          setStoryTitle(file.name.replace('.txt', ''));
        }
      };
      reader.readAsText(file);
    } else {
      toast({
        title: "Fehler",
        description: "Bitte wählen Sie eine .txt Datei",
        variant: "destructive"
      });
    }
  };

  const getReadinessStatus = (score) => {
    if (score >= 0.8) return { color: 'text-green-600', icon: CheckCircle, text: 'Bereit' };
    if (score >= 0.6) return { color: 'text-yellow-600', icon: Clock, text: 'Teilweise bereit' };
    return { color: 'text-red-600', icon: AlertTriangle, text: 'Nicht bereit' };
  };

  // Initial Load
  useEffect(() => {
    fetchGeneratedStories();
    // Lade Analyse für alle Level
    Object.keys(LANGUAGE_LEVELS).forEach(level => {
      fetchLevelAnalysis(level);
    });
  }, []);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Story-Generator</h1>
            <p className="text-gray-600 mt-2">
              Generieren Sie Stories für spezifische Sprachlevel basierend auf Ihrer Wörterdatenbank
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('generator')}
            className={`px-4 py-2 rounded-md transition-colors ${
              activeTab === 'generator' 
                ? 'bg-white text-[#4ECDC4] shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Wand2 className="h-4 w-4 inline mr-2" />
            Story Generieren
          </button>
          <button
            onClick={() => setActiveTab('analysis')}
            className={`px-4 py-2 rounded-md transition-colors ${
              activeTab === 'analysis' 
                ? 'bg-white text-[#4ECDC4] shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <BarChart3 className="h-4 w-4 inline mr-2" />
            Level-Analyse
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 rounded-md transition-colors ${
              activeTab === 'history' 
                ? 'bg-white text-[#4ECDC4] shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <BookOpen className="h-4 w-4 inline mr-2" />
            Generierte Stories
          </button>
        </div>

        {/* Generator Tab */}
        {activeTab === 'generator' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Panel */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Upload className="h-5 w-5 mr-2" />
                  Story-Eingabe
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Story-Titel</Label>
                  <Input
                    id="title"
                    placeholder="Geben Sie einen Titel ein..."
                    value={storyTitle}
                    onChange={(e) => setStoryTitle(e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="file-upload">Story-Datei hochladen (.txt)</Label>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".txt"
                    onChange={handleFileUpload}
                    className="mt-1 block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded file:border-0
                      file:text-sm file:font-semibold
                      file:bg-[#4ECDC4] file:text-white
                      hover:file:bg-[#45b7aa]"
                  />
                </div>

                <div>
                  <Label htmlFor="story-text">Original Story</Label>
                  <Textarea
                    id="story-text"
                    placeholder="Fügen Sie hier Ihre Story ein oder laden Sie eine Datei hoch..."
                    value={originalStory}
                    onChange={(e) => setOriginalStory(e.target.value)}
                    rows={8}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Wörter: {originalStory.split(' ').filter(w => w.trim()).length}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="language">Sprache</Label>
                    <Select value={sourceLanguage} onValueChange={setSourceLanguage}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(LANGUAGES).map(([code, name]) => (
                          <SelectItem key={code} value={code}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="level">Ziel-Level</Label>
                    <Select value={targetLevel} onValueChange={setTargetLevel}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(LANGUAGE_LEVELS).map(([level, name]) => (
                          <SelectItem key={level} value={level}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="max-words">Max. Wörter: {maxWords}</Label>
                  <input
                    id="max-words"
                    type="range"
                    min="50"
                    max="500"
                    step="10"
                    value={maxWords}
                    onChange={(e) => setMaxWords(parseInt(e.target.value))}
                    className="w-full mt-2"
                  />
                </div>

                <Button
                  onClick={generateStory}
                  disabled={isGenerating || !originalStory.trim()}
                  className="w-full bg-[#4ECDC4] hover:bg-[#45b7aa]"
                >
                  {isGenerating ? (
                    <>
                      <Clock className="h-4 w-4 mr-2 animate-spin" />
                      Generiere Story...
                    </>
                  ) : (
                    <>
                      <Wand2 className="h-4 w-4 mr-2" />
                      Story Generieren
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Output Panel */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  Generierte Story
                </CardTitle>
              </CardHeader>
              <CardContent>
                {generatedStory ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Level:</span>
                        <Badge className="ml-2 bg-green-500 text-white">
                          {generatedStory.target_level}
                        </Badge>
                      </div>
                      <div>
                        <span className="font-medium">Schwierigkeit:</span>
                        <span className="ml-2">{generatedStory.difficulty_score}/6.0</span>
                      </div>
                      <div>
                        <span className="font-medium">Original:</span>
                        <span className="ml-2">{generatedStory.original_story_length} Wörter</span>
                      </div>
                      <div>
                        <span className="font-medium">Generiert:</span>
                        <span className="ml-2">{generatedStory.generated_story_length} Wörter</span>
                      </div>
                    </div>

                    <div>
                      <Label>Generierte Story:</Label>
                      <div className="mt-2 p-4 bg-gray-50 rounded-lg border">
                        <h3 className="font-bold text-lg mb-2">{generatedStory.title}</h3>
                        <p className="whitespace-pre-wrap">{generatedStory.content}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Verwendete Wörter (Beispiele):</Label>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {generatedStory.words_used.slice(0, 10).map((word, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {word}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label>Grammatik-Themen:</Label>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {generatedStory.grammar_topics_used.map((topic, index) => (
                            <Badge key={index} className="bg-blue-100 text-blue-800 text-xs">
                              {topic}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm">
                        <Download className="h-4 w-4 mr-2" />
                        Herunterladen
                      </Button>
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4 mr-2" />
                        Vorschau
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Noch keine Story generiert</p>
                    <p className="text-sm">Füllen Sie die Felder links aus und klicken Sie auf "Story Generieren"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Level Analysis Tab */}
        {activeTab === 'analysis' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(LANGUAGE_LEVELS).map(([level, levelName]) => {
              const analysis = levelAnalysis[level];
              if (!analysis) return null;

              const readinessStatus = getReadinessStatus(analysis.readiness_score);
              const StatusIcon = readinessStatus.icon;

              return (
                <Card key={level}>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between">
                      <span>{levelName}</span>
                      <Badge 
                        className={`${readinessStatus.color} bg-transparent border-current`}
                        variant="outline"
                      >
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {readinessStatus.text}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Gesamt Wörter</span>
                      <Badge variant="secondary">{analysis.total_words}</Badge>
                    </div>

                    <div>
                      <span className="text-sm font-medium text-gray-700">Nach Wortart:</span>
                      <div className="mt-2 space-y-1">
                        {Object.entries(analysis.by_word_type || {}).map(([type, data]) => (
                          <div key={type} className="flex justify-between text-xs">
                            <span className="capitalize">{type}</span>
                            <span>{data.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <span className="text-sm font-medium text-gray-700">Empfehlungen:</span>
                      <ul className="mt-2 space-y-1">
                        {analysis.recommendations.map((rec, index) => (
                          <li key={index} className="text-xs text-gray-600">
                            • {rec}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="pt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-[#4ECDC4] h-2 rounded-full transition-all"
                          style={{ width: `${analysis.readiness_score * 100}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Bereitschaft: {Math.round(analysis.readiness_score * 100)}%
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Generated Stories History Tab */}
        {activeTab === 'history' && (
          <Card>
            <CardHeader>
              <CardTitle>Generierte Stories</CardTitle>
            </CardHeader>
            <CardContent>
              {generatedStories.length > 0 ? (
                <div className="space-y-4">
                  {generatedStories.map((story) => (
                    <div key={story.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold">{story.title}</h3>
                        <div className="flex items-center space-x-2">
                          <Badge className="bg-green-500 text-white">{story.target_level}</Badge>
                          <Badge variant="outline">{LANGUAGES[story.source_language]}</Badge>
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm mb-2">
                        {story.content.substring(0, 150)}...
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{story.generated_story_length} Wörter</span>
                        <span>Schwierigkeit: {story.difficulty_score}/6.0</span>
                        <span>{new Date(story.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Noch keine Stories generiert</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default StoryGeneratorAdmin;